<template>
  <div class="car-search-form">
    <!-- Pick-up Location with Airport/City Toggle -->
    <div class="field">
      <b-field :type="errors.pickupLocation ? 'is-danger' : ''" :message="errors.pickupLocation">
        <div class="location-input-group">
          <div class="location-toggle">
            <b-button
              :type="airportSearch ? 'is-primary' : 'is-light'"
              size="is-small"
              @click="toggleLocationSearch(true)"
            >
              <b-icon icon="airplane" size="is-small"></b-icon>
              <span>Airport</span>
            </b-button>
            <b-button
              :type="!airportSearch ? 'is-primary' : 'is-light'"
              size="is-small"
              @click="toggleLocationSearch(false)"
            >
              <b-icon icon="map-marker" size="is-small"></b-icon>
              <span>City</span>
            </b-button>
          </div>

          <div class="location-input" v-if="airportSearch">
            <b-autocomplete
              v-model="pickupLocationInput"
              :data="pickupAirportResults"
              placeholder="Pick-up airport"
              icon="airplane"
              :loading="pickupLoading"
              @typing="searchPickupAirports"
              @select="selectPickupAirport"
              @blur="validatePickupLocation"
              field="displayName"
              :keep-first="true"
              expanded
            >
              <template #default="props">
                <div class="media">
                  <div class="media-content">
                    <strong>{{ props.option.code }}</strong> - {{ props.option.airportName }}
                    <br>
                    <small>{{ props.option.city }}, {{ props.option.state || props.option.country }}</small>
                  </div>
                </div>
              </template>
            </b-autocomplete>
          </div>

          <div class="location-input" v-else>
            <b-input
              v-model="pickupLocationInput"
              placeholder="Pick-up city or address"
              icon="map-marker"
              @blur="validatePickupLocation"
              ref="pickupLocationInput"
              expanded
            />
          </div>
        </div>
      </b-field>
    </div>

    <!-- Different Drop-off Location Toggle -->
    <div class="field is-small mt-4 mb-5">
      <b-checkbox v-model="differentDropoff">
        Return to a different location
      </b-checkbox>
    </div>

    <div class="field mb-6" v-if="differentDropoff">
      <b-field :type="errors.dropoffLocation ? 'is-danger' : ''" :message="errors.dropoffLocation">
        <div class="location-input-group">
          <div class="location-toggle">
            <b-button
              :type="airportSearchDropoff ? 'is-primary' : 'is-light'"
              size="is-small"
              @click="toggleDropoffLocationSearch(true)"
            >
              <b-icon icon="airplane" size="is-small"></b-icon>
              <span>Airport</span>
            </b-button>
            <b-button
              :type="!airportSearchDropoff ? 'is-primary' : 'is-light'"
              size="is-small"
              @click="toggleDropoffLocationSearch(false)"
            >
              <b-icon icon="map-marker" size="is-small"></b-icon>
              <span>City</span>
            </b-button>
          </div>

          <div class="location-input" v-if="airportSearchDropoff">
            <b-autocomplete
              v-model="dropoffLocationInput"
              :data="dropoffAirportResults"
              placeholder="Drop-off airport"
              icon="airplane"
              :loading="dropoffLoading"
              @typing="searchDropoffAirports"
              @select="selectDropoffAirport"
              @blur="validateDropoffLocation"
              field="displayName"
              :keep-first="true"
              expanded
            >
              <template #default="props">
                <div class="media">
                  <div class="media-content">
                    <strong>{{ props.option.code }}</strong> - {{ props.option.airportName }}
                    <br>
                    <small>{{ props.option.city }}, {{ props.option.state || props.option.country }}</small>
                  </div>
                </div>
              </template>
            </b-autocomplete>
          </div>

          <div class="location-input" v-else>
            <b-input
              v-model="dropoffLocationInput"
              placeholder="Drop-off city or address"
              icon="map-marker"
              @blur="validateDropoffLocation"
              ref="dropoffLocationInput"
              expanded
            />
          </div>
        </div>
      </b-field>
    </div>

    <!-- Date and Time Fields -->
    <div class="datetime-field-label">
      <!-- <b-icon icon="car" class="mr-2"></b-icon> -->
      Pick-up date / time
    </div>
    <div class="columns pickup-datetime">
      <div class="column">
        <b-field :type="errors.pickupDate ? 'is-danger' : ''" :message="errors.pickupDate">
          <DateSelector
            v-model="pickupDate"
            placeholder="Pick-up date"
            segmentType="car"
            :dateIndex="0"
            :minDate="minDate"
            :maxDate="maxDate"
            :focusedDate="focusedDate"
            @input="validatePickupDate"
          />
        </b-field>
      </div>
      <div class="column">
        <b-field>
          <b-select
            v-model="pickupTime"
            placeholder="Pick-up time"
            expanded
          >
            <option disabled value="">Please select a time</option>
            <option
              v-for="option in timeOptions"
              :value="option.value"
              :key="option.value"
            >
              {{ option.label }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>

    <div class="datetime-field-label mt-5">
      <!-- <b-icon icon="flag-checkered" class="mr-2"></b-icon> -->
      Drop-off date / time
    </div>
    <div class="columns dropoff-datetime">
      <div class="column">
        <b-field :type="errors.dropoffDate ? 'is-danger' : ''" :message="errors.dropoffDate">
          <DateSelector
            v-model="dropoffDate"
            placeholder="Drop-off date"
            segmentType="car"
            :dateIndex="1"
            :minDate="pickupDate || minDate"
            :maxDate="maxDate"
            :focusedDate="focusedDate"
            @input="validateDropoffDate"
          />
        </b-field>
      </div>
      <div class="column">
        <b-field>
          <b-select
            v-model="dropoffTime"
            placeholder="Drop-off time"
            expanded
          >
            <option disabled value="">Please select a time</option>
            <option
              v-for="option in timeOptions"
              :value="option.value"
              :key="option.value"
            >
              {{ option.label }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>

    <!-- Advanced Options Toggle -->
    <!--<b-collapse v-model="showAdvanced" class="mb-4">
      <template #trigger>
        <b-button type="is-text" size="is-small">
          {{ showAdvanced ? 'Hide' : 'Show' }} Advanced Options
          <b-icon :icon="showAdvanced ? 'chevron-up' : 'chevron-down'"></b-icon>
        </b-button>
      </template>

      <div class="advanced-options mt-3">
        <div class="field">
          <b-field>
            <b-select v-model="rentalCompany" placeholder="Rental company (optional)" expanded>
              <option value="">Any Company</option>
              <option value="hertz">Hertz</option>
              <option value="avis">Avis</option>
              <option value="budget">Budget</option>
              <option value="enterprise">Enterprise</option>
              <option value="national">National</option>
            </b-select>
          </b-field>
        </div>
      </div>
    </b-collapse>-->

    <!-- Search Button -->
    <div class="field mt-5">
      <b-button 
        type="is-primary" 
        expanded 
        @click="handleSearch"
        :loading="searching"
        :disabled="!isFormValid"
      >
        <b-icon icon="magnify" class="mr-2"></b-icon>
        Search Cars
      </b-button>
    </div>
  </div>
</template>

<style scoped>
.location-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.location-toggle {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.location-toggle .button {
  border-radius: 4px;
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
  height: 2.75em;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.location-toggle .button .icon {
  margin-right: 0;
}

.location-input {
  flex: 1;
  min-width: 0;
}

.location-input .autocomplete,
.location-input .field {
  margin-bottom: 0;
}

/* Ensure the input field takes full width */
.location-input .control {
  width: 100%;
}

.location-input .input {
  width: 100%;
}

/* Date/Time Field Labels */
.datetime-field-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #363636;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.datetime-field-label .icon {
  color: #3273dc;
}
</style>

<script>
import GoogleMapsService from '@/services/GoogleMapsService.js'
import DateSelector from '../DateComponents/DateSelector.vue'

export default {
  name: 'CarSearchForm',
  components: {
    DateSelector
  },
  data() {
    return {
      pickupLocation: null,
      dropoffLocation: null,
      pickupLocationInput: '',
      dropoffLocationInput: '',
      differentDropoff: false,
      pickupDate: null,
      dropoffDate: null,
      pickupTime: "12:00", // Default to 12:00 PM
      dropoffTime: "12:00", // Default to 12:00 PM
      rentalCompany: '',
      airportSearch: true,
      airportSearchDropoff: true,
      showAdvanced: false,
      searching: false,
      errors: {},
      pickupAirportResults: [],
      dropoffAirportResults: [],
      pickupLoading: false,
      dropoffLoading: false,
      pickupGooglePlace: null,
      dropoffGooglePlace: null,
      timeOptions: [
        { value: "00:00", label: "12:00 midnight" },
        { value: "00:30", label: "12:30 am" },
        { value: "01:00", label: "1:00 am" },
        { value: "01:30", label: "1:30 am" },
        { value: "02:00", label: "2:00 am" },
        { value: "02:30", label: "2:30 am" },
        { value: "03:00", label: "3:00 am" },
        { value: "03:30", label: "3:30 am" },
        { value: "04:00", label: "4:00 am" },
        { value: "04:30", label: "4:30 am" },
        { value: "05:00", label: "5:00 am" },
        { value: "05:30", label: "5:30 am" },
        { value: "06:00", label: "6:00 am" },
        { value: "06:30", label: "6:30 am" },
        { value: "07:00", label: "7:00 am" },
        { value: "07:30", label: "7:30 am" },
        { value: "08:00", label: "8:00 am" },
        { value: "08:30", label: "8:30 am" },
        { value: "09:00", label: "9:00 am" },
        { value: "09:30", label: "9:30 am" },
        { value: "10:00", label: "10:00 am" },
        { value: "10:30", label: "10:30 am" },
        { value: "11:00", label: "11:00 am" },
        { value: "11:30", label: "11:30 am" },
        { value: "12:00", label: "12:00 noon" },
        { value: "12:30", label: "12:30 pm" },
        { value: "13:00", label: "1:00 pm" },
        { value: "13:30", label: "1:30 pm" },
        { value: "14:00", label: "2:00 pm" },
        { value: "14:30", label: "2:30 pm" },
        { value: "15:00", label: "3:00 pm" },
        { value: "15:30", label: "3:30 pm" },
        { value: "16:00", label: "4:00 pm" },
        { value: "16:30", label: "4:30 pm" },
        { value: "17:00", label: "5:00 pm" },
        { value: "17:30", label: "5:30 pm" },
        { value: "18:00", label: "6:00 pm" },
        { value: "18:30", label: "6:30 pm" },
        { value: "19:00", label: "7:00 pm" },
        { value: "19:30", label: "7:30 pm" },
        { value: "20:00", label: "8:00 pm" },
        { value: "20:30", label: "8:30 pm" },
        { value: "21:00", label: "9:00 pm" },
        { value: "21:30", label: "9:30 pm" },
        { value: "22:00", label: "10:00 pm" },
        { value: "22:30", label: "10:30 pm" },
        { value: "23:00", label: "11:00 pm" },
        { value: "23:30", label: "11:30 pm" },
      ]
    }
  },
  computed: {
    minDate() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    },
    maxDate() {
      const today = new Date()
      return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 340)
    },
    focusedDate() {
      if (this.pickupDate) {
        return this.pickupDate
      } else {
        return new Date()
      }
    },
    isFormValid() {
      return this.pickupLocation &&
             this.pickupDate &&
             this.dropoffDate &&
             (!this.differentDropoff || this.dropoffLocation) &&
             Object.keys(this.errors).length === 0
    },
    airports() {
      return this.$store.state.bookTravel?.airports || []
    }
  },
  methods: {
    toggleLocationSearch(isAirport) {
      this.airportSearch = isAirport
      this.pickupLocation = null
      this.pickupLocationInput = ''
      this.$delete(this.errors, 'pickupLocation')
    },

    toggleDropoffLocationSearch(isAirport) {
      this.airportSearchDropoff = isAirport
      this.dropoffLocation = null
      this.dropoffLocationInput = ''
      this.$delete(this.errors, 'dropoffLocation')
    },

    async initializePickupGooglePlaces() {
      try {
        // First ensure Google Maps API is loaded
        await GoogleMapsService.load()

        const Library = await this.$store.dispatch("hotelSearch/getPlacesLibrary")
        const input = this.$refs.pickupLocationInput?.$el?.querySelector('input')

        console.log('Initializing pickup Google Places:', { Library, input, ref: this.$refs.pickupLocationInput })

        if (input) {
          const options = {
            types: ['(cities)'],
            fields: ['name', 'geometry', 'formatted_address']
          }

          const autocomplete = new Library.Autocomplete(input, options)

          autocomplete.addListener("place_changed", () => {
            this.pickupGooglePlace = autocomplete.getPlace()
            console.log('Pickup place changed:', this.pickupGooglePlace)

            if (this.pickupGooglePlace && this.pickupGooglePlace.geometry) {
              this.pickupLocationInput = this.pickupGooglePlace.name || this.pickupGooglePlace.formatted_address

              // Set pickup location with coordinates
              const location = this.pickupGooglePlace.geometry.location
              let lat, lng
              if (typeof location.lat === 'function') {
                lat = location.lat()
                lng = location.lng()
              } else {
                lat = location.lat
                lng = location.lng
              }

              this.pickupLocation = {
                name: this.pickupGooglePlace.formatted_address || this.pickupGooglePlace.name,
                lat: lat,
                lng: lng
              }

              console.log('Set pickup location:', this.pickupLocation)
              this.validatePickupLocation()
            }
          })
        } else {
          console.error('Could not find pickup input element')
        }
      } catch (error) {
        console.error('Error initializing Google Places for pickup:', error)
      }
    },

    async initializeDropoffGooglePlaces() {
      try {
        // First ensure Google Maps API is loaded
        await GoogleMapsService.load()

        const Library = await this.$store.dispatch("hotelSearch/getPlacesLibrary")
        const input = this.$refs.dropoffLocationInput?.$el?.querySelector('input')

        console.log('Initializing dropoff Google Places:', { Library, input, ref: this.$refs.dropoffLocationInput })

        if (input) {
          const options = {
            types: ['(cities)'],
            fields: ['name', 'geometry', 'formatted_address']
          }

          const autocomplete = new Library.Autocomplete(input, options)

          autocomplete.addListener("place_changed", () => {
            this.dropoffGooglePlace = autocomplete.getPlace()
            console.log('Dropoff place changed:', this.dropoffGooglePlace)

            if (this.dropoffGooglePlace && this.dropoffGooglePlace.geometry) {
              this.dropoffLocationInput = this.dropoffGooglePlace.name || this.dropoffGooglePlace.formatted_address

              // Set dropoff location with coordinates
              const location = this.dropoffGooglePlace.geometry.location
              let lat, lng
              if (typeof location.lat === 'function') {
                lat = location.lat()
                lng = location.lng()
              } else {
                lat = location.lat
                lng = location.lng
              }

              this.dropoffLocation = {
                name: this.dropoffGooglePlace.formatted_address || this.dropoffGooglePlace.name,
                lat: lat,
                lng: lng
              }

              console.log('Set dropoff location:', this.dropoffLocation)
              this.validateDropoffLocation()
            }
          })
        } else {
          console.error('Could not find dropoff input element')
        }
      } catch (error) {
        console.error('Error initializing Google Places for dropoff:', error)
      }
    },

    searchPickupAirports(name) {
      if (!name || name.length < 2) {
        this.pickupAirportResults = []
        return
      }

      this.pickupLoading = true
      const results = this.airports.filter(airport =>
        airport.detailedName?.toUpperCase().includes(name.toUpperCase()) ||
        airport.code?.toUpperCase().includes(name.toUpperCase())
      ).slice(0, 10).map(airport => ({
        ...airport,
        displayName: `${airport.code} - ${airport.airportName}`
      }))

      this.pickupAirportResults = results
      this.pickupLoading = false
    },

    searchDropoffAirports(name) {
      if (!name || name.length < 2) {
        this.dropoffAirportResults = []
        return
      }

      this.dropoffLoading = true
      const results = this.airports.filter(airport =>
        airport.detailedName?.toUpperCase().includes(name.toUpperCase()) ||
        airport.code?.toUpperCase().includes(name.toUpperCase())
      ).slice(0, 10).map(airport => ({
        ...airport,
        displayName: `${airport.code} - ${airport.airportName}`
      }))

      this.dropoffAirportResults = results
      this.dropoffLoading = false
    },

    selectPickupAirport(airport) {
      this.pickupLocation = airport
      this.pickupLocationInput = airport ? `${airport.code} - ${airport.airportName}` : ''
      this.validatePickupLocation()
    },

    selectDropoffAirport(airport) {
      this.dropoffLocation = airport
      this.dropoffLocationInput = airport ? `${airport.code} - ${airport.airportName}` : ''
      this.validateDropoffLocation()
    },



    validatePickupLocation() {
      if (this.airportSearch) {
        if (!this.pickupLocation) {
          this.$set(this.errors, 'pickupLocation', 'Please select a pick-up airport')
        } else {
          this.$delete(this.errors, 'pickupLocation')
        }
      } else {
        if (!this.pickupLocationInput.trim()) {
          this.$set(this.errors, 'pickupLocation', 'Please enter a pick-up location')
        } else if (!this.pickupLocation || !this.pickupLocation.lat) {
          // If no Google Place was selected, create basic location object
          this.pickupLocation = { name: this.pickupLocationInput }
          this.$delete(this.errors, 'pickupLocation')
        } else {
          this.$delete(this.errors, 'pickupLocation')
        }
      }
    },

    validateDropoffLocation() {
      if (this.differentDropoff) {
        if (this.airportSearchDropoff) {
          if (!this.dropoffLocation) {
            this.$set(this.errors, 'dropoffLocation', 'Please select a drop-off airport')
          } else {
            this.$delete(this.errors, 'dropoffLocation')
          }
        } else {
          if (!this.dropoffLocationInput.trim()) {
            this.$set(this.errors, 'dropoffLocation', 'Please enter a drop-off location')
          } else if (!this.dropoffLocation || !this.dropoffLocation.lat) {
            // If no Google Place was selected, create basic location object
            this.dropoffLocation = { name: this.dropoffLocationInput }
            this.$delete(this.errors, 'dropoffLocation')
          } else {
            this.$delete(this.errors, 'dropoffLocation')
          }
        }
      } else {
        this.$delete(this.errors, 'dropoffLocation')
      }
    },
    validatePickupDate() {
      if (!this.pickupDate) {
        this.$set(this.errors, 'pickupDate', 'Please select a pick-up date')
      } else {
        // Normalize dates for comparison by setting time to start of day
        const selectedDate = new Date(this.pickupDate)
        selectedDate.setHours(0, 0, 0, 0)
        const minDate = new Date(this.minDate)
        minDate.setHours(0, 0, 0, 0)

        if (selectedDate < minDate) {
          this.$set(this.errors, 'pickupDate', 'Please select a future date')
        } else {
          this.$delete(this.errors, 'pickupDate')
          // Auto-set dropoff date if not set
          if (!this.dropoffDate) {
            const nextDay = new Date(this.pickupDate)
            nextDay.setDate(nextDay.getDate() + 1)
            this.dropoffDate = nextDay
          }
        }
      }
    },
    validateDropoffDate() {
      if (!this.dropoffDate) {
        this.$set(this.errors, 'dropoffDate', 'Please select a drop-off date')
      } else if (this.dropoffDate < this.pickupDate) {
        this.$set(this.errors, 'dropoffDate', 'Drop-off date must be after pick-up date')
      } else {
        this.$delete(this.errors, 'dropoffDate')
      }
    },
    handleSearch() {
      // Validate all fields
      this.validatePickupLocation()
      this.validateDropoffLocation()
      this.validatePickupDate()
      this.validateDropoffDate()

      if (this.isFormValid) {
        this.searching = true

        // Set default traveller settings for business travel
        this.$store.commit('travellersAndTrips/EDIT_TRAVELLERS', {
          tripType: 'business',
          bookFor: 'user',
          set: true
        })

        const searchData = {
          pickupLocation: this.pickupLocation,
          dropoffLocation: this.differentDropoff ? this.dropoffLocation : this.pickupLocation,
          pickupDate: this.pickupDate,
          dropoffDate: this.dropoffDate,
          pickupTime: this.pickupTime,
          dropoffTime: this.dropoffTime,
          rentalCompany: this.rentalCompany,
          differentDropoff: this.differentDropoff,
          airportSearch: this.airportSearch,
          airportSearchDropoff: this.airportSearchDropoff
        }

        this.$emit('search', searchData)

        // Reset searching state after a delay
        setTimeout(() => {
          this.searching = false
        }, 1000)
      }
    }
  },

  async mounted() {
    console.log('CarSearchForm mounted', { airportSearch: this.airportSearch, differentDropoff: this.differentDropoff })

    // Wait for DOM to be ready
    await this.$nextTick()

    // Only initialize Google Places for city inputs if they are visible
    if (!this.airportSearch) {
      try {
        await this.initializePickupGooglePlaces()
        console.log('Pickup Google Places initialized')
      } catch (error) {
        console.error('Failed to initialize pickup Google Places:', error)
      }
    }

    // Initialize dropoff Google Places when differentDropoff is enabled and not using airport search
    if (this.differentDropoff && !this.airportSearchDropoff) {
      try {
        await this.initializeDropoffGooglePlaces()
        console.log('Dropoff Google Places initialized')
      } catch (error) {
        console.error('Failed to initialize dropoff Google Places:', error)
      }
    }
  },

  watch: {
    async differentDropoff(newVal) {
      if (!newVal) {
        this.dropoffLocation = null
        this.dropoffLocationInput = ''
        this.$delete(this.errors, 'dropoffLocation')
      } else {
        // Initialize Google Places for dropoff when enabled and not using airport search
        if (!this.airportSearchDropoff) {
          await this.$nextTick()
          try {
            await this.initializeDropoffGooglePlaces()
            console.log('Dropoff Google Places initialized via differentDropoff watch')
          } catch (error) {
            console.error('Failed to initialize dropoff Google Places via differentDropoff watch:', error)
          }
        }
      }
    },

    async airportSearch(newVal) {
      if (!newVal) {
        // Switched to city search - initialize Google Places for pickup
        await this.$nextTick()
        try {
          await this.initializePickupGooglePlaces()
          console.log('Pickup Google Places initialized via airportSearch watch')
        } catch (error) {
          console.error('Failed to initialize pickup Google Places via airportSearch watch:', error)
        }
      }
    },

    async airportSearchDropoff(newVal) {
      if (!newVal && this.differentDropoff) {
        // Switched to city search for dropoff - initialize Google Places
        await this.$nextTick()
        try {
          await this.initializeDropoffGooglePlaces()
          console.log('Dropoff Google Places initialized via airportSearchDropoff watch')
        } catch (error) {
          console.error('Failed to initialize dropoff Google Places via airportSearchDropoff watch:', error)
        }
      }
    }
  }
}
</script>

<style scoped>
.location-input-group {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.location-input {
  flex: 1;
}

.advanced-options {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .columns {
    margin: 0;
    display: block;
  }

  .column {
    padding: 0.5rem 0;
    width: 100% !important;
  }

  .advanced-options {
    padding-top: 0.75rem;
  }

  /* Stack toggle buttons on their own row for mobile */
  .location-input-group {
    flex-direction: column;
    gap: 0.75rem;
  }

  .location-toggle {
    display: flex;
    width: 100%;
    gap: 0.5rem;
  }

  .location-toggle .button {
    flex: 1;
    width: 100%;
    max-width: none;
  }

  .location-input {
    width: 100%;
  }

  /* Keep date/time pairs on same line */
  .pickup-datetime,
  .dropoff-datetime {
    display: flex !important;
    margin: 0;
  }

  .pickup-datetime .column,
  .dropoff-datetime .column {
    padding: 0 0.25rem;
    width: 50% !important;
  }

  /* Ensure all other form controls are full width */
  .field .control,
  .field .control .input,
  .field .control .datepicker,
  .field .control .timepicker {
    width: 100%;
  }
}
</style>
